import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";

const PaymentRecord = ({ products }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const allSet = products?.set || [];
  const orderId = products._id;
  
  const personEmail = products?.email;

  const [paymentData, setPaymentData] = useState([]);
  const [totalPayAmount, setTotalPayAmount] = useState(0);

  const invoicePaymentId = products?.paymentId;

  useEffect(() => {
    if (personEmail && invoicePaymentId) {
      const fetchPaymentData = async () => {
        try {
          const response = await axios.get(
            `https://server-gs.vercel.app/paid/stripe/${personEmail}/${invoicePaymentId}`
          );
          setPaymentData(response.data);

          // Calculate the total payAmount if available
          const total = response.data.reduce(
            (acc, payment) => acc + (parseFloat(payment.payAmount) || 0),
            0
          );
          setTotalPayAmount(total?.toFixed(2)); // Ensure two decimal places
        } catch (err) {
          console.error("Error fetching payment data:", err);
          setError("Failed to fetch payment data");
        }
      };

      fetchPaymentData();
    }
  }, [personEmail, invoicePaymentId]);

  useEffect(() => {
    if (personEmail) {
      const fetchPaymentData = async () => {
        try {
          const response = await axios.get(
            `https://server-gs.vercel.app/paid/stripe/${personEmail}`
          );
          setPaymentData(response.data);

          // Calculate the total payAmount if available
          const total = response.data.reduce(
            (acc, payment) => acc + (parseFloat(payment.payAmount) || 0),
            0
          );
          setTotalPayAmount(total.toFixed(2)); // Ensure two decimal places
        } catch (err) {
          console.error("Error fetching payment data:", err);
          setError("Failed to fetch payment data");
        }
      };

      fetchPaymentData();
    }
  }, [personEmail]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://server-gs.vercel.app/event-company-profile"
        );
        setData(response.data);
        setError(null); // Clear any previous errors
      } catch (err) {
        setError(err.message || "Something went wrong");
      } finally {
        // setLoading(false);
      }
    };

    // Fetch data every second
    const intervalId = setInterval(fetchData, 1000);

    // Cleanup the interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const [paymentMethod, setPaymentMethod] = useState("");
  const [payableAmount, setPayableAmount] = useState(0); // State for payable amount
  const [paymentDate, setPaymentDate] = useState(""); // State for payment date
  const [projectSelectDate, setProjectSelectedDate] = useState("");
  const [projectSelectedHour, setProjectSelectedHour] = useState("12");
  const [projectSelectedMinute, setProjectSelectedMinute] = useState("00");
  const [projectSelectedPeriod, setProjectSelectedPeriod] = useState("AM");

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Calculate the final total
    const finalTotal = parseFloat(calculateFinalTotal(allSet, products));

    // Calculate the sum of totalPayAmount and payableAmount
    const totalPayment = parseFloat(totalPayAmount) + parseFloat(payableAmount);

    // Check if the total payment exceeds the final total
    if (totalPayment > finalTotal) {
      await Swal.fire({
        title: "Invalid Payment!",
        text: "The total payment amount cannot exceed the final total.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return; // Stop the function if the total payment is invalid
    }

    // Calculate the total tax for all sets
    const totalTax = parseFloat(calculateAllSetTax(allSet));

    // Show SweetAlert confirmation dialog
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to proceed with the payment?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed!",
      cancelButtonText: "Cancel",
    });

    // If the user cancels, stop the function
    if (!result.isConfirmed) {
      return;
    }

    const [startYear, startMonth, startDay] = projectSelectDate.split("-");
    let startHour = parseInt(projectSelectedHour, 10);
    const startMinute = parseInt(projectSelectedMinute, 10);

    // Convert 12-hour format to 24-hour format
    if (projectSelectedPeriod === "PM" && startHour < 12) startHour += 12;
    if (projectSelectedPeriod === "AM" && startHour === 12) startHour = 0;

    // Create Date object in local time zone
    const combinedStartDate = new Date(
      startYear,
      startMonth - 1,
      startDay,
      startHour,
      startMinute
    );

    // Ensure it's converted to a proper ISO format before sending
    const formattedStartDate = combinedStartDate.toISOString();


    const paymentData = {
      invoicePaymentId: products?.paymentId,
      email: products?.email,
      name: `${products?.firstName} ${products?.lastName}`,
      address: products?.address,
      phoneNumber: products?.phoneNumber,
      tax: totalTax, // Include the total tax in the payment data
      payAmount: payableAmount,
      paymentMethod,
      PaymentDate: combinedStartDate,
    };

    try {
      // Send payment data to backend
      const paymentResponse = await fetch("https://server-gs.vercel.app/stripe-pay", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(paymentData),
      });

      if (!paymentResponse.ok) {
        throw new Error("Payment submission failed");
      }

      // Calculate the updated paidStripeAmount locally
      const existingPaidAmount = parseFloat(products?.paidStripeAmount) || 0;
      const newPaidAmount = existingPaidAmount + parseFloat(payableAmount);

      // Determine the status based on the updated paidStripeAmount and amountDue
      const amountDue = calculateAmountDue(allSet, products, totalPayAmount);
      let status = "partial";
       
      if (newPaidAmount >= parseFloat(amountDue)) {
        status = "paid";
      }

      // Update the order with the new paidStripeAmount and status
      const orderResponse = await fetch(
        `https://server-gs.vercel.app/order-paid/${products?._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paidStripeAmount: newPaidAmount.toString(), // Ensure it's saved as a string
            paymentMethod,
            PaymentDate: combinedStartDate,
            status, // Include the status in the update
          }),
        }
      );

      if (!orderResponse.ok) {
        throw new Error("Order update failed");
      }

      // Show success message
      await Swal.fire({
        title: "Success!",
        text: "Payment submitted successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });

      window.location.reload();
    } catch (error) {
      console.error("Error:", error);

      // Show error message
      await Swal.fire({
        title: "Error!",
        text: "An error occurred. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const calculateTotalPrice = (pro) => {
    let priceAfterDiscount = pro.selectedPricingValue;

    // Apply discount to the selectedPricingValue first
    if (pro?.discount) {
      const discountValue =
        pro.discountType === "%"
          ? (parseFloat(pro.discount) / 100) * priceAfterDiscount
          : parseFloat(pro.discount);

      priceAfterDiscount -= discountValue;
    }

    // Multiply the discounted price by the quantity
    let total = pro.quantity * priceAfterDiscount;

    // Add tax after applying discount and calculating the total
    if (pro?.tax) {
      const taxValue = (parseFloat(pro.tax) / 100) * total;
      total += taxValue;
    }

    // Ensure total is not negative
    return Math.max(0, total).toFixed(2);
  };

  const calculateSubTotalPrice = (pro) => {
    return pro.quantity * pro.selectedPricingValue;
  };

  const calculateSetSubTotal = (products) => {
    return products
      .reduce((acc, pro) => acc + calculateSubTotalPrice(pro), 0)
      .toFixed(2);
  };

  const calculateSetTotal = (products) => {
    return products
      .reduce((acc, pro) => acc + parseFloat(calculateTotalPrice(pro)), 0)
      .toFixed(2);
  };

  const calculateDiscountTotal = (products) => {
    return products
      .reduce((acc, pro) => {
        if (pro?.discount) {
          let discountValue = 0;

          if (pro.discountType === "%") {
            // For percentage discount, calculate based on the subtotal
            const subtotal = pro.quantity * pro.selectedPricingValue;
            discountValue = (parseFloat(pro.discount) / 100) * subtotal;
          } else if (pro.discountType === "$") {
            // For fixed amount discount, apply the discount directly (do not multiply by quantity)
            discountValue = parseFloat(pro.discount);
          }

          acc += discountValue; // Accumulate the discount value
        }
        return acc;
      }, 0)
      .toFixed(2); // Return the total discount rounded to 2 decimal places
  };

  // Function to calculate total tax
  const calculateTaxTotal = (products) => {
    return products
      .reduce((totalTax, product) => {
        // Get base price or default to 0
        const price = parseFloat(product.selectedPricingValue) || 0;
        const quantity = parseInt(product.quantity) || 1;

        // Apply discount if exists
        let priceAfterDiscount = price;
        if (product?.discount) {
          const discountAmount = parseFloat(product.discount) || 0;
          const discountValue =
            product.discountType === "%"
              ? (discountAmount / 100) * priceAfterDiscount
              : discountAmount;
          priceAfterDiscount = Math.max(0, priceAfterDiscount - discountValue);
        }

        // Calculate subtotal after discount
        const subtotal = quantity * priceAfterDiscount;

        // Calculate tax for this product if tax exists
        const productTax = product?.tax
          ? (parseFloat(product.tax) / 100) * subtotal
          : 0;

        return totalTax + productTax;
      }, 0)
      .toFixed(2);
  };

  const calculateAllSetSubTotal = (allSet) => {
    return allSet
      .reduce((total, perSet) => {
        // Calculate the subtotal for each set, using the correct array of products
        const setTotal = parseFloat(calculateSetSubTotal(perSet.products)); // Fix here
        return total + setTotal;
      }, 0)
      .toFixed(2);
  };

  const calculateAllSetTotal = (allSet) => {
    return allSet
      .reduce((total, perSet) => {
        const setTotal = perSet.products.reduce(
          (acc, pro) => acc + parseFloat(calculateTotalPrice(pro)),
          0
        );
        return total + setTotal;
      }, 0)
      .toFixed(2);
  };

  const calculateSingleTotalPrice = (pro) => {
    if (!pro || typeof pro !== "object") return "0.00";

    const quantity = Number(pro.quantity) || 0;
    const price = Number(pro.selectedPricingValue) || 0;

    const total = quantity * price;

    return total > 0 ? total.toFixed(2) : "0.00";
  };

  const calculateWithDiscountPrice = (pro) => {
    let priceAfterDiscount = pro.selectedPricingValue;

    // Apply discount to the selectedPricingValue first
    if (pro?.discount) {
      const discountValue =
        pro.discountType === "%"
          ? (parseFloat(pro.discount) / 100) * priceAfterDiscount
          : parseFloat(pro.discount);

      priceAfterDiscount -= discountValue;
    }

    // Multiply the discounted price by the quantity
    let total = pro.quantity * priceAfterDiscount;

    // Ensure total is not negative
    return Math.max(0, total).toFixed(2);
  };

  const calculateProductDiscountPrice = (pro) => {
    const totalWithoutDiscount = parseFloat(calculateSingleTotalPrice(pro));
    const totalWithDiscount = parseFloat(calculateWithDiscountPrice(pro));

    // Calculate the difference
    const discountAmount = totalWithoutDiscount - totalWithDiscount;

    // Ensure the discount amount is not negative
    return Math.max(0, discountAmount).toFixed(2);
  };

  const calculateAllSetDiscount = (allSet) => {
    return allSet
      .reduce((total, perSet) => {
        const setDiscount = perSet.products.reduce((acc, pro) => {
          return acc + parseFloat(calculateProductDiscountPrice(pro));
        }, 0);

        return total + setDiscount;
      }, 0)
      .toFixed(2); // Round to 2 decimal places
  };

  const calculateTotalTaxAmount = (pro) => {
    // Convert all numerical values to numbers
    const price = parseFloat(pro.selectedPricingValue) || 0;
    const quantity = parseInt(pro.quantity) || 1;

    let priceAfterDiscount = price;

    // Apply discount if exists
    if (pro?.discount) {
      const discountAmount = parseFloat(pro.discount) || 0;
      const discountValue =
        pro.discountType === "%"
          ? (discountAmount / 100) * priceAfterDiscount
          : discountAmount;

      priceAfterDiscount = Math.max(0, priceAfterDiscount - discountValue);
    }

    // Calculate subtotal (after discount)
    const subtotal = quantity * priceAfterDiscount;

    // Calculate tax amount if tax exists
    if (pro?.tax) {
      const taxRate = parseFloat(pro.tax) || 0;
      return ((taxRate / 100) * subtotal).toFixed(2);
    }

    return "0.00"; // Return 0 if no tax
  };

  // Function to calculate All Set Tax
  const calculateAllSetTax = (allSet) => {
    return allSet
      .reduce((totalSetsTax, perSet) => {
        // Calculate tax for all products in this set
        const setTax = perSet.products.reduce(
          (productTaxAccumulator, product) => {
            // Get base price or default to 0
            const price = parseFloat(product.selectedPricingValue) || 0;
            const quantity = parseInt(product.quantity) || 1;

            // Apply discount if exists
            let priceAfterDiscount = price;
            if (product?.discount) {
              const discountAmount = parseFloat(product.discount) || 0;
              const discountValue =
                product.discountType === "%"
                  ? (discountAmount / 100) * priceAfterDiscount
                  : discountAmount;
              priceAfterDiscount = Math.max(
                0,
                priceAfterDiscount - discountValue
              );
            }

            // Calculate subtotal after discount
            const subtotal = quantity * priceAfterDiscount;

            // Calculate tax for this product if tax exists
            return (
              productTaxAccumulator +
              (product?.tax ? (parseFloat(product.tax) / 100) * subtotal : 0)
            );
          },
          0
        );

        return totalSetsTax + setTax;
      }, 0)
      .toFixed(2); // Total tax for all sets
  };

  const calculateFinalTotal = (allSet, products) => {
    // Calculate the total for all sets
    const total = calculateAllSetTotal(allSet);

    // Get the discount value and type
    const discount = parseFloat(products?.AllProductDiscount) || 0;
    const discountType = products?.AllProductDiscountType;

    let finalTotal = parseFloat(total);

    // Apply discount based on the discount type
    if (discountType === "$") {
      // Subtract fixed amount discount
      finalTotal -= discount;
    } else if (discountType === "%") {
      // Subtract percentage discount
      finalTotal -= (discount / 100) * finalTotal;
    }

    // Ensure the final total is not negative
    return Math.max(0, finalTotal).toFixed(2);
  };

  const calculateDeposit = (finalTotal, advanceDeposit) => {
    // Convert advanceDeposit to a percentage value
    const depositPercentage = parseFloat(advanceDeposit) || 0;

    // Calculate the deposit amount
    const depositAmount = (depositPercentage / 100) * parseFloat(finalTotal);

    // Ensure the deposit amount is not negative
    return Math.max(0, depositAmount).toFixed(2);
  };

  const calculateAmountDue = (allSet, products, totalPayAmount) => {
    const finalTotal = parseFloat(calculateFinalTotal(allSet, products)); // Call the function
    return (finalTotal - totalPayAmount).toFixed(2); // Subtract totalPayAmount
  };

  const amountDue = calculateAmountDue(allSet, products, totalPayAmount);

  const calculatePayFinalAmount = (
    allSet,
    products,
    totalPayAmount,
    advanceDeposit
  ) => {
    const finalTotal = parseFloat(calculateFinalTotal(allSet, products)); // Calculate final total
    const depositAmount = parseFloat(
      calculateDeposit(finalTotal, advanceDeposit)
    ); // Calculate deposit amount
    const amountDue = parseFloat(
      calculateAmountDue(allSet, products, totalPayAmount)
    ); // Calculate amount due

    // Check if deposit matches the total payment amount
    if (depositAmount > 0) {
      return amountDue;
    }

    // Check if amount due is zero
    if (amountDue === 0) {
      return "Payment Complete";
    }

    return depositAmount;
  };

  // Example Usage
  const payFinalAmount = calculatePayFinalAmount(
    allSet,
    products,
    totalPayAmount,
    products?.AdvanceDeposit
  );

  const [isLoading, setIsLoading] = useState(false);

  const updatePaymentInBackend = async () => {
    if (!orderId) return;

    setIsLoading(true);
    setError(null);

    try {
      const totalAmount = calculateFinalTotal(allSet, products);
      const amountDue = calculateAmountDue(allSet, products, totalPayAmount);

      await axios.put('https://server-gs.vercel.app/add-payment-history', {
        orderId,
        totalAmount,
        amountPaid: totalPayAmount,
        amountDue
      });

      console.log('Payment updated successfully');
    } catch (err) {
      console.error('Failed to update payment:', err);
      setError('Failed to update payment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };



  useEffect(() => {
    if (orderId  && allSet) {
      const timer = setTimeout(() => {
        updatePaymentInBackend();
      }, 100); // Small delay to avoid too frequent updates

      return () => clearTimeout(timer);
    }
  }, [orderId, allSet, totalPayAmount]);

  return (
    <div className="bg-blue-100 p-10">
      <div className="mx-auto w-full bg-white h-[800px] flex flex-row justify-between gap-10 shadow-md  border border-gray-200 rounded-xl">
        <div className="w-[60%] h-full flex justify-center items-center">
          
          <form
            onSubmit={handleSubmit}
            className="w-[70%] border p-6 bg-white shadow-md rounded-lg"
          >
            {/* Customer Details Section */}
            <div className="mb-6">
              <h1 className="text-lg font-roboto text-gray-900">
                <span className="font-semibold">Name:</span>{" "}
                {products.firstName} {products.lastName}
              </h1>
              <h1 className="text-xl  font-roboto text-gray-900">
                <span className="font-semibold">Phone:</span>{" "}
                {products.phoneNumber}
              </h1>
              <h1 className="text-xl  font-roboto text-gray-900">
                <span className="font-semibold">Email:</span> {products.email}
              </h1>
            </div>

            {/* Payable Amount Input */}
            <div className="mb-4">
              <label className="block text-md font-medium font-roboto text-gray-700 mb-1">
                Payable Amount:
              </label>
              <input
                type="number"
                value={payableAmount}
                onChange={(e) => setPayableAmount(parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 font-roboto rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Payment Method Dropdown */}
            <div className="mb-4">
              <label className="block text-md font-medium font-roboto text-gray-700 mb-1">
                Payment Method:
              </label>
              <select
  value={paymentMethod}
  onChange={(e) => setPaymentMethod(e.target.value)}
  className="w-full px-3 py-2 border border-gray-300 font-roboto rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
  required
>
  <option selected value="">Select Option</option>
  <option value="cash">Cash</option>
  <option value="bank transfer">Bank Transfer</option>
  <option value="check">Check</option>
  <option value="zelle">Zelle</option>
  <option value="cash app">Cashapp</option>
  <option value="venmo">Venmo</option>
  <option value="debit card">Debit Card</option>
  <option value="credit card">Credit Card</option>
  <option value="other">Other</option>
</select>
            </div>

            {/* Payment Date Input */}
            {/* <div className="mb-6">
              <label className="block text-md font-medium font-roboto text-gray-700 mb-1">
                Payment Date:
              </label>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 font-roboto rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
         
              />
            </div> */}
            {/* Start Date and Time */}
            <div className="grid grid-cols-1 gap-5 w-full">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={projectSelectDate}
                  onChange={(e) => setProjectSelectedDate(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <div className="flex gap-2">
                  <select
                    value={projectSelectedHour}
                    onChange={(e) => setProjectSelectedHour(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((hour) => (
                      <option key={hour} value={hour < 10 ? `0${hour}` : hour}>
                        {hour < 10 ? `0${hour}` : hour}
                      </option>
                    ))}
                  </select>
                  <select
                    value={projectSelectedMinute}
                    onChange={(e) => setProjectSelectedMinute(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    {Array.from({ length: 12 }, (_, i) => i * 5).map(
                      (minute) => (
                        <option
                          key={minute}
                          value={minute < 10 ? `0${minute}` : minute}
                        >
                          {minute < 10 ? `0${minute}` : minute}
                        </option>
                      )
                    )}
                  </select>
                  <select
                    value={projectSelectedPeriod}
                    onChange={(e) => setProjectSelectedPeriod(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div> */}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full mt-5 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Submit Payment
            </button>
          </form>
        </div>
        <div className="w-full lg:w-[40%] bg-white p-8 rounded-xl shadow-lg border border-gray-200">
          <h1 className="text-2xl font-bold text-gray-800 border-b border-gray-300 pb-4">
            Payment Summary
          </h1>
          <div className="space-y-4 mt-6">
            <div className="flex justify-between text-lg text-gray-700">
              <span>Sub Total:</span>
              <span>${calculateAllSetSubTotal(allSet)}</span>
            </div>
            {calculateAllSetDiscount(allSet) !== 0 &&
              calculateAllSetDiscount(allSet) !== "0.00" && (
                <div className="flex justify-between text-lg text-gray-700 border-t pt-4">
                  <span>Discount:</span>
                  <span className="text-red-600">
                    - ${calculateAllSetDiscount(allSet)}
                  </span>
                </div>
              )}
            {calculateAllSetTax(allSet) !== 0 &&
              calculateAllSetTax(allSet) !== "0.00" && (
                <div className="flex justify-between text-lg text-gray-700 border-t pt-4">
                  <span>Tax:</span>
                  <span>${calculateAllSetTax(allSet)}</span>
                </div>
              )}

            <div className="flex justify-between text-lg text-gray-700 border-t pt-4">
              <span>Total:</span>
              <span>${calculateAllSetTotal(allSet)}</span>
            </div>
            {products?.AllProductDiscount &&
              products?.AllProductDiscount !== "0" && (
                <div className="flex justify-between text-lg text-gray-700 border-t pt-4">
                  <span>Discount on Total:</span>
                  <span>
                    {products?.AllProductDiscount}{" "}
                    {products?.AllProductDiscountType}
                  </span>
                </div>
              )}
            {products?.AllProductDiscount &&
              products?.AllProductDiscount !== "0" && (
                <div className="flex justify-between text-lg text-gray-700 border-t pt-4">
                  <span>Final Total:</span>
                  <span>${calculateFinalTotal(allSet, products)}</span>
                </div>
              )}

            <div className="flex justify-between text-lg text-gray-700 border-t pt-4">
              <span>
                Deposit ({products?.advanceDepositValue}{" "}
                {products?.advanceDepositType}):
              </span>
              <span>
                ${" "}
                {calculateDeposit(
                  calculateFinalTotal(allSet, products),
                  products?.advanceDepositValue,
                  products?.advanceDepositType
                )}
              </span>
            </div>
            <div className="flex justify-between text-lg text-gray-700 border-t pt-4">
              <span>Amount Paid:</span>
              <span>$ {totalPayAmount}</span>
            </div>
            <div className="flex justify-between text-lg text-gray-700 border-t pt-4">
              <span>Amount Due:</span>
              <span>$ {amountDue}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentRecord;
